// store.js
import { createStore, action } from 'easy-peasy';
import tokenManager from '../utils/tokenManager';

const parseJwt = (token) => {
  try {
    const base64Url = token?.split('.')[1];
    const base64 = base64Url?.replace(/-/g, '+')?.replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        ?.split('')
        .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
};

const authModel = {
  token: null, // Inicialmente o token é nulo
  isAuthenticated: false, // Estado de autenticação
  tenantId: null, // Inicialmente o tenant é nulo
  tenantLabel: null, // Inicialmente o tenant é nulo
  makerVersion: null, // Versão selecionada do Maker
  ownerTenantCodes: [], // Lista de codes de tenants que são system owners (do JWT)
  isPersonalizationMode: false, // true quando o tenant atual NÃO é system owner
  personalizationTenantId: null, // tenantId quando em modo personalização
  user: {
    email: null, // Inicialmente o email do usuário é nulo
    telephone: null, // Inicialmente o telefone do usuário é nulo
    confirmedEmail: false, // Inicialmente o email confirmado é nulo
    confirmedTelephone: false, // Inicialmente o telefone confirmado é nulo
  },
  login: action((state, userData) => {
    state.token = userData.token;
    tokenManager.setToken(userData.token); // Salva o token no localStorage e cookie compartilhado
    state.user.email = userData.email; // Salva o email do usuário
    state.user.telephone = userData.telephone; // Salva o telefone do usuário
    state.user.confirmedEmail = userData.confirmedEmail; // Salva o status de confirmação do email
    state.user.confirmedTelephone = userData.confirmedTelephone; // Salva o status de confirmação do telefone
    // Extrai ownerTenantCodes do JWT no momento do login
    const payload = parseJwt(userData.token);
    state.ownerTenantCodes = Array.isArray(payload?.ownerTenantCodes) ? payload.ownerTenantCodes : [];
  }),
  authenticate: action((state, tenant) => {
    state.isAuthenticated = true;
    state.tenantId = tenant?.key;
    state.tenantLabel = tenant.label;
    // Determina modo personalização com base em ownerTenantCodes
    const isOwner = state.ownerTenantCodes.includes(tenant?.key);
    state.isPersonalizationMode = !isOwner;
    state.personalizationTenantId = isOwner ? null : tenant?.key;
    // Salva dados de tenant em cookies compartilhados
    tokenManager.setTenantData(tenant?.key, tenant.label);
  }),
  setMakerVersion: action((state, version) => {
    state.makerVersion = version;
    if (version) {
      localStorage.setItem('makerVersion', version);
    } else {
      localStorage.removeItem('makerVersion');
    }
  }),
  setOwnerTenantCodes: action((state, codes) => {
    state.ownerTenantCodes = Array.isArray(codes) ? codes : [];
  }),
  logout: action((state) => {
    state.token = null;
    state.isAuthenticated = false;
    state.tenantId = null;
    state.tenantLabel = null;
    state.makerVersion = null;
    state.ownerTenantCodes = [];
    state.isPersonalizationMode = false;
    state.personalizationTenantId = null;
    localStorage.removeItem('makerVersion');
    tokenManager.removeToken(); // Remove o token do localStorage e cookie compartilhado
  }),
  initializeAuth: action((state) => {
    const token = tokenManager.getToken(); // Recupera token do cookie ou localStorage
    if (token) {
      state.token = token;
      state.isAuthenticated = true;

      // Recupera ownerTenantCodes do JWT
      const payload = parseJwt(token);
      state.ownerTenantCodes = Array.isArray(payload?.ownerTenantCodes) ? payload.ownerTenantCodes : [];

      // Recupera dados de tenant se existirem
      const tenantData = tokenManager.getTenantData();
      if (tenantData) {
        state.tenantId = tenantData.tenantId;
        state.tenantLabel = tenantData.tenantLabel;
        // Reaplica modo personalização com base no tenant atual
        const isOwner = state.ownerTenantCodes.includes(tenantData.tenantId);
        state.isPersonalizationMode = !isOwner;
        state.personalizationTenantId = isOwner ? null : tenantData.tenantId;
      }

      // Recupera makerVersion se existir
      const savedVersion = localStorage.getItem('makerVersion');
      if (savedVersion) {
        state.makerVersion = savedVersion;
      }
    }
  }),
};

// Removido persist - tokenManager agora é a única fonte de verdade
const store = createStore({
  auth: authModel,
});

export default store;
